import React, { useEffect, useState } from 'react'
import { Patient } from '@dataTypes/PatientInfo.types'
import { Row, Col, Form } from 'react-bootstrap'
import InputField from '@components/common/InputField/InputField.component'
import { PatientWithInsurance } from '@dataTypes/appointment.type'
import { familyMemberOptions } from '@constants/PatientManagement/options'
import { searchPatientsByName } from '@services/api/patient.api'
import { AppDispatch } from '@store/index'
import { useDispatch } from 'react-redux'
import { updatePatient } from '@store/actions'
import { getFullName } from '@utils/fullname.util'
import { TooltipComponent } from '@syncfusion/ej2-react-popups'
import {
  mockFamilyMembers,
  FamilyMemberInfo,
} from '@assets/mockdata/PatientTray/mockFamilyMembers'
import { shouldUsePatientTrayMockData } from '@utils/patientTrayMocks.util'
import { ReactComponent as FamilyAddIcon } from '@assets/images/Sidepanel/family-add.svg'
import { ReactComponent as FamilyEditIcon } from '@assets/images/Sidepanel/family-edit.svg'
import { ReactComponent as FamilyGenderFemaleIcon } from '@assets/images/Sidepanel/family-gender-female.svg'
import { ReactComponent as FamilyGenderMaleIcon } from '@assets/images/Sidepanel/family-gender-male.svg'
import ConfirmDeleteModal from '../Modals/ConfirmDeleteModal/ConfirmDeleteModal.component'

import { logger } from '@utils/safeLogger'
import {
  Badge,
  Button,
  DataTable,
  type DataTableColumn,
  type DataTableRow,
  type DataTableSortDirection,
} from '@enamelai/design-system'
import { useTheme } from '@context/ThemeContext'

export const genderIcon = (gender: string): JSX.Element => {
  switch (gender) {
    case 'Male':
      return (
        <FamilyGenderMaleIcon className="sp-family-gender-icon" aria-hidden />
      )
    case 'Female':
      return (
        <FamilyGenderFemaleIcon className="sp-family-gender-icon" aria-hidden />
      )
    default:
      return <span className="sp-family-gender-icon" aria-hidden />
  }
}

type FamilyMembersProps = {
  patient: Patient | null
  isLoading?: boolean
}

type MemberStatus = 'Active' | 'Inactive'

type FamilyMemberRow = DataTableRow & {
  name: string
  relation: string
  status: MemberStatus
}

const getMemberStatus = (member: FamilyMemberInfo): MemberStatus =>
  member.status ?? 'Active'

const FamilySectionHeader: React.FC<{
  isEditing: boolean
  isAdding: boolean
  onAdd: () => void
  onEdit: () => void
  onDone: () => void
  onSave: () => void
  onCancelAdd: () => void
}> = ({ isEditing, isAdding, onAdd, onEdit, onDone, onSave, onCancelAdd }) => (
  <div className="sp-family-section-header">
    <span className="sp-family-section-title">Family members</span>
    <div className="sp-family-header-actions">
      {isAdding ? (
        <>
          <TooltipComponent content="Save" position="TopCenter">
            <button
              type="button"
              className="sp-family-icon-btn sp-family-icon-btn--primary"
              onClick={onSave}
              aria-label="Save family member"
            >
              <i className="mgc mgc_check_line" aria-hidden />
            </button>
          </TooltipComponent>
          <TooltipComponent content="Cancel" position="TopCenter">
            <button
              type="button"
              className="sp-family-icon-btn"
              onClick={onCancelAdd}
              aria-label="Cancel"
            >
              <i className="mgc mgc_close_line" aria-hidden />
            </button>
          </TooltipComponent>
        </>
      ) : isEditing ? (
        <TooltipComponent content="Done" position="TopCenter">
          <button
            type="button"
            className="sp-family-icon-btn sp-family-icon-btn--primary"
            onClick={onDone}
            aria-label="Done editing"
          >
            <i className="mgc mgc_check_line" aria-hidden />
          </button>
        </TooltipComponent>
      ) : (
        <>
          <TooltipComponent content="Add member" position="TopCenter">
            <button
              type="button"
              className="sp-family-icon-btn"
              onClick={onAdd}
              aria-label="Add family member"
            >
              <FamilyAddIcon className="sp-family-action-svg" aria-hidden />
            </button>
          </TooltipComponent>
          <TooltipComponent content="Edit" position="TopCenter">
            <button
              type="button"
              className="sp-family-icon-btn"
              onClick={onEdit}
              aria-label="Edit family members"
            >
              <FamilyEditIcon className="sp-family-action-svg" aria-hidden />
            </button>
          </TooltipComponent>
        </>
      )}
    </div>
  </div>
)

const FamilyMembers: React.FC<FamilyMembersProps> = ({
  patient,
  isLoading = false,
}) => {
  const dispatch: AppDispatch = useDispatch()
  const { resolvedTheme } = useTheme()
  const [nameSortDirection, setNameSortDirection] =
    useState<DataTableSortDirection>('asc')
  const [isEditing, setIsEditing] = useState(false)
  const [isAdding, setIsAdding] = useState(false)
  const [newFamilyMember, setNewFamilyMember] = useState<{
    family_member_id: string
    family_member_name: string
    relation_to_patient: string
  }>({
    family_member_id: '',
    family_member_name: '',
    relation_to_patient: '',
  })
  const [debouncedName, setDebouncedName] = useState('')
  const [patients, setPatients] = useState<PatientWithInsurance[]>([])
  const [mockFamilyMembersState, setMockFamilyMembersState] = useState<
    FamilyMemberInfo[]
  >([])
  const [memberToDelete, setMemberToDelete] = useState<FamilyMemberInfo | null>(
    null,
  )

  const useMockFamilyData = shouldUsePatientTrayMockData(patient?.patient_id)
  const familyMembersInfo: FamilyMemberInfo[] = useMockFamilyData
    ? mockFamilyMembersState
    : ((patient?.family_members_info as FamilyMemberInfo[] | undefined) ?? [])

  useEffect(() => {
    if (patient && useMockFamilyData) {
      setMockFamilyMembersState(mockFamilyMembers[patient.patient_id] || [])
    } else {
      setMockFamilyMembersState([])
    }
  }, [patient, useMockFamilyData])

  useEffect(() => {
    if (isAdding) {
      const input = document.querySelector('.input-field.patient-name-input')
      if (input) {
        ;(input as HTMLInputElement).focus()
      }
    }
  }, [isAdding])

  const fetchPatients = async (name: string): Promise<void> => {
    try {
      const existingMemberIds = familyMembersInfo
        .map((member: FamilyMemberInfo) => member.patient_member_id)
        .join(',')
      const response = await searchPatientsByName(
        name,
        existingMemberIds || undefined,
      )
      if (response.statusCode === 200) {
        setPatients(response.data?.patients)
      } else {
        setPatients([])
      }
    } catch (error) {
      logger.error('Error fetching patients:', error)
      setPatients([])
    }
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      if (debouncedName.trim()) {
        void fetchPatients(debouncedName.trim())
      } else {
        setPatients([])
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [debouncedName])

  const patientNameHandleChange = (value: string): void => {
    setNewFamilyMember({ ...newFamilyMember, family_member_name: value })
    setDebouncedName(value)
  }

  const handlePatientSelect = (pat: PatientWithInsurance): void => {
    setNewFamilyMember({
      ...newFamilyMember,
      family_member_id: pat.patient_id,
      family_member_name: getFullName(pat, true),
    })
    setPatients([])
  }

  const relationToPatientHandleChange = (value: string): void => {
    setNewFamilyMember({ ...newFamilyMember, relation_to_patient: value })
  }

  const addNewFamilyMemberElement = (): JSX.Element => (
    <Row className="add-family-member-row sp-family-add-row">
      <Col md={8} className="col">
        <InputField
          value={newFamilyMember.family_member_name}
          placeholder="Search"
          leftIcon={<i className="ti ti-search" />}
          onChange={(e) => patientNameHandleChange(e.target.value)}
          className="patient-name-input"
        />
      </Col>
      <Col md={4} className="col">
        <Form.Group
          controlId="formConditions"
          className="relation-to-patient-content"
        >
          <Form.Select
            value={newFamilyMember.relation_to_patient}
            onChange={(e) => relationToPatientHandleChange(e.target.value)}
          >
            <option value="">Relation</option>
            {familyMemberOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </Col>
      {patients.length > 0 && (
        <ul className="patient-dd-list">
          {patients.map((pat) => (
            <li
              key={pat.patient_id}
              onClick={() => handlePatientSelect(pat)}
              className="patient-dropdown-item"
            >
              {pat.first_name} {pat.last_name}
            </li>
          ))}
        </ul>
      )}
    </Row>
  )

  const updateFamilyMembers = (memberPatientId?: string): Patient => {
    if (useMockFamilyData) {
      if (isAdding) {
        setMockFamilyMembersState([...mockFamilyMembersState])
      } else if (memberPatientId) {
        setMockFamilyMembersState(
          mockFamilyMembersState.filter(
            (member) => member.patient_member_id !== memberPatientId,
          ),
        )
      }
      return patient!
    }

    const updatedPatientData: Patient = { ...patient! }
    if (
      patient?.family_members_info &&
      patient.family_members_info.length > 0
    ) {
      const relatives = patient.family_members_info.map(
        (member: FamilyMemberInfo) => ({
          family_member_id: member.patient_member_id,
          family_member_name: getFullName(member.patient_member_info, true),
          relation_to_patient: member.relation_to_patient,
        }),
      )
      if (isAdding) {
        updatedPatientData.relatives = relatives
        updatedPatientData.relatives?.push(newFamilyMember)
      } else {
        updatedPatientData.relatives = relatives.filter(
          (member: { family_member_id: string }) =>
            member.family_member_id !== memberPatientId,
        )
      }
    } else {
      updatedPatientData.relatives = []
      if (isAdding) {
        updatedPatientData.relatives?.push(newFamilyMember)
      }
    }

    return updatedPatientData
  }

  const handleSetDefaultData = (): void => {
    setIsAdding(false)
    setIsEditing(false)
    setNewFamilyMember({
      family_member_id: '',
      family_member_name: '',
      relation_to_patient: '',
    })
    setDebouncedName('')
    setPatients([])
  }

  const handleUpdatePatientData = (
    patientData: Patient,
    setDefault = false,
  ): void => {
    if (useMockFamilyData) {
      if (setDefault) {
        handleSetDefaultData()
      }
      return
    }
    dispatch(updatePatient(patientData)).then(() => {
      if (setDefault) {
        handleSetDefaultData()
      }
    })
  }

  const handleSaveNewFamilyMember = (): void => {
    handleUpdatePatientData(updateFamilyMembers(), true)
  }

  const handleDeleteFamilyMember = (memberPatientId: string): void => {
    handleUpdatePatientData(updateFamilyMembers(memberPatientId))
  }

  const memberDisplayName = (member: FamilyMemberInfo | null): string => {
    if (!member) return 'this family member'
    const info = member.patient_member_info
    const name = [info?.last_name, info?.first_name].filter(Boolean).join(', ')
    return name || 'this family member'
  }

  const handleConfirmDeleteFamilyMember = (): void => {
    if (!memberToDelete) return
    handleDeleteFamilyMember(memberToDelete.patient_member_id)
    setMemberToDelete(null)
  }

  const handleAddClick = (): void => {
    setIsAdding(true)
    setNewFamilyMember({
      family_member_id: '',
      family_member_name: '',
      relation_to_patient: '',
    })
    setDebouncedName('')
    setPatients([])
  }

  const members: FamilyMemberInfo[] = familyMembersInfo

  const familyRows: FamilyMemberRow[] = members
    .map((member) => ({
      id: member.patient_member_id,
      name: `${member.patient_member_info.last_name}, ${member.patient_member_info.first_name}`,
      relation: member.relation_to_patient,
      status: getMemberStatus(member),
    }))
    .sort((a, b) => {
      if (!nameSortDirection) return 0
      const order = a.name.localeCompare(b.name)
      return nameSortDirection === 'asc' ? order : -order
    })

  const familyColumns: DataTableColumn<FamilyMemberRow>[] = [
    {
      id: 'name',
      label: 'Name',
      sortable: true,
      renderCell: (row) => <span className="sp-family-name">{row.name}</span>,
    },
    {
      id: 'relation',
      label: 'Relation',
      width: 80,
      renderCell: (row) => (
        <span className="sp-family-relation">{row.relation}</span>
      ),
    },
    {
      id: 'status',
      label: 'Status',
      width: 80,
      align: 'center',
      renderCell: (row) => (
        <Badge
          label={row.status}
          color={row.status === 'Active' ? 'vivid-green' : 'red'}
          mode={resolvedTheme === 'dark' ? 'solid' : 'tinted'}
        />
      ),
    },
  ]

  return (
    <div className="sp-family family-member-container">
      <FamilySectionHeader
        isEditing={isEditing}
        isAdding={isAdding}
        onAdd={handleAddClick}
        onEdit={() => setIsEditing(true)}
        onDone={() => setIsEditing(false)}
        onSave={handleSaveNewFamilyMember}
        onCancelAdd={() => setIsAdding(false)}
      />
      {isAdding && addNewFamilyMemberElement()}
      <DataTable<FamilyMemberRow>
        className="sp-family-table"
        columns={familyColumns}
        rows={familyRows}
        state={
          isLoading && members.length === 0
            ? 'loading'
            : members.length === 0
              ? 'empty'
              : 'default'
        }
        emptyState={{ title: 'No family members to display' }}
        sortColumnId={nameSortDirection ? 'name' : undefined}
        sortDirection={nameSortDirection}
        onSort={() =>
          setNameSortDirection((current) =>
            current === 'asc' ? 'desc' : 'asc',
          )
        }
        showActions={isEditing}
        actionsLabel=""
        renderActions={(row) => (
          <Button
            variant="primary"
            size="xs"
            destructive
            onClick={() =>
              setMemberToDelete(
                members.find((m) => m.patient_member_id === row.id) ?? null,
              )
            }
          >
            Delete
          </Button>
        )}
      />
      <ConfirmDeleteModal
        show={memberToDelete !== null}
        onHide={() => setMemberToDelete(null)}
        onConfirm={handleConfirmDeleteFamilyMember}
        entityName={memberDisplayName(memberToDelete)}
      />
    </div>
  )
}

export default FamilyMembers
